import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import { collection, orderBy, query, where } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useFirestoreQuery } from '../../hooks/useFirestoreQuery';
import {
  ANNOUNCEMENT_ROLE_LABELS,
  ANNOUNCEMENT_TARGET_ROLES,
  createAnnouncement,
  formatAnnouncementDate,
} from '../../lib/announcements';

/**
 * Company-internal outreach — broadcast messages to workspace roles.
 */
export default function TeamBroadcastsPanel({ userData }) {
  const { workspace, companyId } = useWorkspace();
  const resolvedCompanyId = companyId || userData?.companyId;
  const isCompanyAdmin = userData?.role === 'company_admin';

  const [broadcastTitle, setBroadcastTitle] = useState('');
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [broadcastTargetRoles, setBroadcastTargetRoles] = useState([]);
  const [broadcasting, setBroadcasting] = useState(false);
  const [broadcastError, setBroadcastError] = useState('');
  const [broadcastSuccess, setBroadcastSuccess] = useState('');

  const announcementsQuery = useMemo(() => {
    if (!resolvedCompanyId || !isCompanyAdmin) return null;
    return query(
      collection(db, 'announcements'),
      where('companyId', '==', resolvedCompanyId),
      orderBy('timestamp', 'desc'),
    );
  }, [resolvedCompanyId, isCompanyAdmin]);

  const { data: recentAnnouncements, loading: announcementsLoading } = useFirestoreQuery(
    announcementsQuery,
    { enabled: Boolean(resolvedCompanyId && isCompanyAdmin) },
  );

  const handleBroadcast = async () => {
    const cid = resolvedCompanyId;
    const actor = auth.currentUser;
    if (!cid || !actor) return;

    setBroadcasting(true);
    setBroadcastError('');
    setBroadcastSuccess('');
    try {
      await createAnnouncement(db, {
        companyId: cid,
        title: broadcastTitle,
        message: broadcastMessage,
        targetRoles: broadcastTargetRoles,
        createdBy: actor.uid,
      });
      setBroadcastTitle('');
      setBroadcastMessage('');
      setBroadcastTargetRoles([]);
      setBroadcastSuccess('Message sent to selected roles.');
    } catch (e) {
      console.error('TeamBroadcastsPanel:', e);
      setBroadcastError(e.message || 'Could not send message.');
    } finally {
      setBroadcasting(false);
    }
  };

  if (!isCompanyAdmin) {
    return (
      <Alert severity="info" sx={{ maxWidth: 560 }}>
        Team outreach is available to Company Admins. You can read messages in Settings → Notifications.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Team outreach
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
        Send targeted messages to admins, developers, managers, and agents inside{' '}
        <strong>{workspace?.name || 'your workspace'}</strong>. Recipients see them in their
        notifications inbox and on sign-in.
      </Typography>

      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <CampaignIcon color="primary" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                New message
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose roles below — only matching users in your company will receive it.
              </Typography>
            </Box>
          </Stack>

          {broadcastError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBroadcastError('')}>
              {broadcastError}
            </Alert>
          )}
          {broadcastSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setBroadcastSuccess('')}>
              {broadcastSuccess}
            </Alert>
          )}

          <Stack spacing={2} sx={{ maxWidth: 640 }}>
            <TextField
              label="Subject"
              value={broadcastTitle}
              onChange={(e) => setBroadcastTitle(e.target.value)}
              fullWidth
              required
              disabled={broadcasting}
              placeholder="e.g. Scheduled maintenance tonight"
            />
            <TextField
              label="Message"
              value={broadcastMessage}
              onChange={(e) => setBroadcastMessage(e.target.value)}
              fullWidth
              required
              multiline
              minRows={4}
              disabled={broadcasting}
              placeholder="Share details your team needs to know…"
            />
            <Autocomplete
              multiple
              options={ANNOUNCEMENT_TARGET_ROLES}
              value={broadcastTargetRoles}
              onChange={(_, value) => setBroadcastTargetRoles(value)}
              getOptionLabel={(role) => ANNOUNCEMENT_ROLE_LABELS[role] || role}
              disabled={broadcasting}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Target roles"
                  placeholder="Select roles…"
                  helperText="Managers, agents, developers, and other internal roles."
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((role, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={role}
                    size="small"
                    label={ANNOUNCEMENT_ROLE_LABELS[role] || role}
                  />
                ))
              }
            />
            <Box>
              <Button
                variant="contained"
                startIcon={broadcasting ? <CircularProgress size={16} color="inherit" /> : <CampaignIcon />}
                onClick={handleBroadcast}
                disabled={
                  broadcasting ||
                  !broadcastTitle.trim() ||
                  !broadcastMessage.trim() ||
                  broadcastTargetRoles.length === 0
                }
              >
                {broadcasting ? 'Sending…' : 'Send message'}
              </Button>
            </Box>
          </Stack>

          {announcementsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3, mt: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : recentAnnouncements.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                Recent outreach
              </Typography>
              <Stack spacing={1.5}>
                {recentAnnouncements.slice(0, 12).map((row) => (
                  <Box
                    key={row.id}
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'divider',
                      bgcolor: 'action.hover',
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {row.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatAnnouncementDate(row.timestamp)}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                      {row.message}
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {(row.targetRoles || []).map((role) => (
                        <Chip
                          key={`${row.id}-${role}`}
                          size="small"
                          variant="outlined"
                          label={ANNOUNCEMENT_ROLE_LABELS[role] || role}
                        />
                      ))}
                      <Chip
                        size="small"
                        label={`${(row.seenBy || []).length} seen`}
                        variant="outlined"
                      />
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
